import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import {
    } from 'lucide-react';


import { Card } from '@/components/ui/card';

/**
 * The document chimney on the right.
 *
 * What is listed here is no longer a set of file names somebody dropped: it is the record of the
 * runs over this project's documents. Dropping a file starts one, and the row for it appears while
 * the conversion is still going — because the record exists from the run's first second.
 *
 * Beside each document stands what is currently happening to it, and, as soon as the second step
 * has decided, what the document *is*. That order matters: the type is the answer the reader came
 * for and it is settled well before the extraction that follows, so it is shown the moment it is
 * known rather than held back until the run is over.
 *
 * There is no list of steps. A stepper made sense in the processing test, where one document is
 * examined closely; here twelve documents are being read at once, and what belongs beside each is
 * one line saying where it has got to.
 */

interface Properties {
    /** Der Körper: der Ordnerbaum des Datenraums. */
    children?: React.ReactNode;
    /** Colour of the step in view, so the chimney follows the rail. */
    color: string;
    tint: string;
    disabled?: boolean;
    /** Why nothing can be dropped, when nothing can. */
    disabledHint?: string;
}


/**
 * What a row says about itself, in one line.
 *
 * The live stream is preferred over the stored record while a run is being watched: it is the same
 * information a second or two earlier, and a second or two is the whole of what this line is for.
 */

export default function DocumentChimney({
    children,
    color,
    tint,
    disabled,
    disabledHint,
}: Properties) {
    const { t } = useTranslation();

    return (
        <Card
            className={cn(
                'flex min-h-[200px] flex-col',
                // Auf breiten Fenstern bis zum unteren Rand: Die Karte klebt oben, also ist ihre
                // Höhe der sichtbare Rest. Erst dadurch hat der Baum darin eine feste Höhe, an der
                // sein Bildlauf hängen kann — vorher gab es nur ein `min-h`, und was darin `h-full`
                // wollte, fiel auf null zusammen.
                'xl:h-[calc(100vh-1.5rem)] xl:min-h-[360px]',
            )}
            style={{ '--step-color': color, '--step-tint': tint } as React.CSSProperties}
        >
            {/* Kein Kopf: Der Baum darunter bringt einen mit — Symbol, Titel, Zählung. Drei
                Überschriften übereinander sagten dreimal dasselbe. */}
            {/* Der Baum ist der Körper des Kamins.
                Die flache Liste stand hier, solange die Analyse ihre Datei im Konversationsordner
                ablegte und der Datenraum den Projektordner zeigte — zwei Listen mit verschiedenen
                Dateien, keine vollständig. Seit die Analyse in den Projektordner ablegt, zeigt der
                Baum beides: die analysierten Dokumente und das, was jemand von Hand abgelegt hat. */}
            {/* Die Ablagefläche ist weg: In den Baum zu ziehen ist der Weg, und das heißt immer
                analysieren. Eine zweite Fläche darüber wäre ein zweiter Weg für dieselbe Sache. */}
            {/* Oben derselbe Absatz wie bei den Nachbarkarten, die ihren Kopf mit `py-2` absetzen —
                nur etwas großzügiger, weil hier kein Rahmen darunter liegt, der die Kante zieht.
                Ohne ihn klebte die Überschrift des Baums an der Kartenkante und lief gegen die
                Köpfe daneben aus der Flucht. */}
            {disabled ? (
                <div className="px-4 pb-3 pt-4 text-center text-xs text-muted-foreground">
                    {disabledHint ?? t('portfolioDashboard.documents.dragHint')}
                </div>
            ) : (
                <div className="min-h-0 flex-1 px-3 pb-3 pt-4">{children}</div>
            )}

        </Card>
    );
}
